/**
 * Staff Layout - Olympic Fit Wellness
 * Layout wrapper for staff portal pages with CMS and Class Management
 */
import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Calendar,
  Users,
  ClipboardList,
  Settings,
  LogOut,
  ChevronDown,
  Bell,
  FileText,
  Image,
  Dumbbell,
  UserPlus,
  Home
} from 'lucide-react';

export function StaffLayout({ children }) {
  const { user, profile, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Main navigation for staff
  const navigation = [
    { name: 'Dashboard', href: '/staff', icon: LayoutDashboard },
    { name: 'My Schedule', href: '/staff/schedule', icon: Calendar },
    { name: 'My Clients', href: '/staff/clients', icon: Users },
  ];

  // Class Management section
  const classManagement = [
    { name: 'Manage Classes', href: '/admin/classes', icon: Dumbbell },
    { name: 'Class Calendar', href: '/admin/calendar', icon: Calendar },
  ];

  // CMS section (accessible by staff)
  const cmsNavigation = [
    { name: 'CMS Pages', href: '/admin/cms/pages', icon: FileText },
    { name: 'Media Library', href: '/admin/cms/media', icon: Image },
  ];

  // Settings
  const settingsNav = [
    { name: 'Settings', href: '/staff/settings', icon: Settings },
  ];

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.href;
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
          isActive 
            ? 'bg-green-600 text-white' 
            : 'text-neutral-600 hover:bg-neutral-100'
        }`}
      >
        <item.icon className="w-5 h-5" />
        <span className="font-medium">{item.name}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="staff-layout">
      {/* Top Nav */}
      <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b border-neutral-200 z-40 flex items-center justify-between px-6">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-xl font-heading font-bold tracking-[0.15em]">
              <span className="text-neutral-900">OF</span>
              <span className="text-[#DC2626]">W</span>
            </span>
            <span className="text-xs font-heading text-neutral-500 tracking-wider">STAFF COCKPIT</span>
          </Link>
        </div>
        
        <div className="flex items-center gap-4">
          <Link 
            to="/portal" 
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg"
          >
            <Home className="w-4 h-4" />
            Member Portal
          </Link>
          
          <button className="relative p-2 hover:bg-neutral-100 rounded-lg">
            <Bell className="w-5 h-5 text-neutral-600" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          
          <div className="relative">
            <button 
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="flex items-center gap-3 pl-4 border-l border-neutral-200"
            >
              <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center">
                <span className="text-white text-sm font-medium">
                  {profile?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || 'S'}
                </span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-neutral-900">{profile?.full_name || 'Staff Member'}</p>
                <p className="text-xs text-neutral-500 capitalize">{profile?.role?.replace('_', ' ') || 'Staff'}</p>
              </div>
              <ChevronDown className="w-4 h-4 text-neutral-400" />
            </button>
            
            {isUserMenuOpen && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-white border border-neutral-200 rounded-lg py-2 shadow-lg">
                <Link
                  to="/portal"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Home className="w-4 h-4" />
                  Member Portal
                </Link>
                <Link
                  to="/staff/settings"
                  className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                  onClick={() => setIsUserMenuOpen(false)}
                >
                  <Settings className="w-4 h-4" />
                  Settings
                </Link>
                <hr className="my-2 border-neutral-100" />
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside className="fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-neutral-200 overflow-y-auto">
          <nav className="p-4 space-y-6">
            {/* Main Navigation */}
            <div>
              <p className="px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Main</p>
              <div className="space-y-1">
                {navigation.map((item) => <NavLink key={item.href} item={item} />)}
              </div>
            </div>

            {/* Class Management */}
            <div>
              <p className="px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Class Management</p>
              <div className="space-y-1">
                {classManagement.map((item) => <NavLink key={item.href} item={item} />)}
              </div>
            </div>

            {/* CMS */}
            <div>
              <p className="px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Content (CMS)</p>
              <div className="space-y-1">
                {cmsNavigation.map((item) => <NavLink key={item.href} item={item} />)}
              </div>
            </div>

            {/* Settings */}
            <div>
              <div className="space-y-1">
                {settingsNav.map((item) => <NavLink key={item.href} item={item} />)}
              </div>
            </div>
          </nav>

          {/* User Role Badge */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-neutral-200 bg-neutral-50">
            <div className="text-center">
              <p className="text-xs text-neutral-500 mb-1">Logged in as</p>
              <p className="font-heading font-bold text-green-600 capitalize">
                {profile?.role?.replace('_', ' ') || 'STAFF'}
              </p>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 ml-64 min-h-screen">
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}

export default StaffLayout;
